import { useDroppable } from "@dnd-kit/core";
import { QueueListIcon } from '@heroicons/react/24/outline';
import { FolderOpenIcon } from '@heroicons/react/24/solid';
import classNames from "classnames";
import { MouseEvent } from 'react';

import { useAppDispatch, useAppSelector } from "../../../store";
import { uiActions } from "../../ui/slice";
import { Button } from '../../ui/components/Button';
import { ConfirmButton } from '../../ui/components/ConfirmButton';

import { usePlaylistAdvancer } from "../hooks";
import { PlaylistEntry } from "./PlaylistEntry";
import { SaveButton } from './SaveButton';
import { currentPlaylistSelector } from "../selectors";
import { playlistActions } from '../slice';
import { PlaylistEntry as PlaylistEntryType } from "../types";

export const Playlist = () => {

  const { isOver, setNodeRef } = useDroppable({
    id: 'playlist',
  });

  const currentPlaylist = useAppSelector(currentPlaylistSelector);
  const liveResource = useAppSelector(state => state.ui.currentLiveResource);
  const resources = useAppSelector(state => state.resources);

  usePlaylistAdvancer();

  const dispatch = useAppDispatch();

  const classes = classNames(
    'overflow-y-auto grow min-h-0 p-1',
    {
      'bg-blue-200': isOver
    }
  );

  const handleClick = (entry: PlaylistEntryType, index: number) => () => {
    const resource = entry.resource || resources[entry.resourceID!];
      dispatch(uiActions.setCurrentPreviewResource({
        resource,
        annotations: {
          playlistIndex: index,
          themeID: entry.themeID
        }
      }));
  };

  const handleDelete = (index: number) => (evt: MouseEvent) => {
    dispatch(playlistActions.removeItem(index));
    evt.preventDefault();
    evt.stopPropagation();
  };

  const handleEdit = (resourceID?: string) => () => {
    if (resourceID && resources[resourceID]) {
      dispatch(uiActions.setCurrentModal({
        modal: 'resourceEditor',
        payload: resources[resourceID]
      }));
    }
  };

  const handleSetTheme = (index: number) => (themeID: string | undefined) => {
    dispatch(playlistActions.setItemTheme({ index, themeID }));
  };

  const handleClear = () => {
    dispatch(playlistActions.clearPlaylist());
  };

  const showLoadPlaylistModal = () => {
    dispatch(uiActions.setCurrentModal({ modal: 'loadPlaylist' }));
  };

  return (
    <>
      <h4 className='text-2xl p-2 flex justify-between items-center'>
        Playlist
        <div className='flex text-sm'>
          <ConfirmButton
            cancelText='Cancel'
            caption='Are you sure you want to clear the current playlist?'
            className='p-1 flex items-center text-nowrap'
            confirmText='Clear playlist'
            onConfirm={handleClear}
            title='New playlist'
          >
            <QueueListIcon className='size-4 mr-1' />
            New
          </ConfirmButton>
          <Button
            onClick={showLoadPlaylistModal}
            title='Load playlist…'
          >
            <FolderOpenIcon className='size-4 mr-1' />
            Load
          </Button>
          <SaveButton currentPlaylist={currentPlaylist} />
        </div>
      </h4>
      <div
        className={classes}
        ref={setNodeRef}
      >
        {
          currentPlaylist.items.map(
            (entry, index) => (
              <PlaylistEntry
                active={index === liveResource?.annotations?.playlistIndex}
                entry={entry}
                index={index}
                key={index}
                onClick={handleClick(entry, index)}
                onDelete={handleDelete(index)}
                onEdit={handleEdit(entry.resource?.id || entry.resourceID)}
                onSetTheme={handleSetTheme(index)}
              />
            )
          )
        }
      </div>
    </>
  );
};
